/**
 * Generative Line Art System
 * Ported from fafo-kit with all original algorithms intact.
 * Each function draws procedural art onto a Canvas2D context.
 */

export function seededRandom(seed) {
  let s = seed;
  return function () {
    s ^= s << 13;
    s ^= s >> 17;
    s ^= s << 5;
    return ((s >>> 0) % 10000) / 10000;
  };
}

function densityToCount(density, minCount, maxCount) {
  return Math.round(minCount + (density / 100) * (maxCount - minCount));
}

/**
 * Main entry point: draws the selected line art style onto the given context.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} w - canvas width
 * @param {number} h - canvas height
 * @param {string} style - one of: flow, grid, circles, topo, spectrograph, waveform, fire, oscillator
 * @param {number} density - 1-100
 * @param {number} opacity - 1-100
 * @param {string} color - CSS color string
 * @param {number} seed - integer seed for reproducibility
 */
export function drawLineArt(ctx, w, h, style, density, opacity, color, seed) {
  if (style === 'none') return;
  ctx.save();
  ctx.globalAlpha = opacity / 100;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = Math.max(1, w * 0.001);
  const rng = seededRandom(seed);
  switch (style) {
    case 'flow': drawFlowField(ctx, w, h, densityToCount(density, 3, 40), rng); break;
    case 'grid': drawBrokenGrid(ctx, w, h, densityToCount(density, 4, 40), rng); break;
    case 'circles': drawOrbitalRings(ctx, w, h, densityToCount(density, 3, 35), rng); break;
    case 'topo': drawTopographic(ctx, w, h, densityToCount(density, 4, 30), rng); break;
    case 'spectrograph': drawSpectrograph(ctx, w, h, densityToCount(density, 6, 50), rng); break;
    case 'waveform': drawWaveform(ctx, w, h, densityToCount(density, 3, 24), rng); break;
    case 'fire': drawFire(ctx, w, h, densityToCount(density, 8, 60), rng); break;
    case 'oscillator': drawOscillator(ctx, w, h, densityToCount(density, 5, 45), rng); break;
    case 'smiley': drawSmiley(ctx, w, h, densityToCount(density, 3, 20), rng); break;
    case 'constellation': drawConstellation(ctx, w, h, densityToCount(density, 15, 80), rng); break;
    case 'circuit': drawCircuit(ctx, w, h, densityToCount(density, 5, 30), rng); break;
    case 'chevron': drawChevron(ctx, w, h, densityToCount(density, 4, 25), rng); break;
    case 'diamonds': drawDiamonds(ctx, w, h, densityToCount(density, 8, 60), rng); break;
    case 'crosshatch': drawCrosshatch(ctx, w, h, densityToCount(density, 5, 35), rng); break;
    case 'dots': drawDots(ctx, w, h, densityToCount(density, 20, 200), rng); break;
    case 'spiral': drawSpiral(ctx, w, h, densityToCount(density, 2, 12), rng); break;
    case 'maze': drawMaze(ctx, w, h, densityToCount(density, 6, 30), rng); break;
    case 'lightning': drawLightning(ctx, w, h, densityToCount(density, 2, 12), rng); break;
    case 'rain': drawRain(ctx, w, h, densityToCount(density, 20, 120), rng); break;
    case 'hexgrid': drawHexgrid(ctx, w, h, densityToCount(density, 4, 20), rng); break;
    case 'scatter': drawScatter(ctx, w, h, densityToCount(density, 30, 200), rng); break;
    case 'waves': drawWaves(ctx, w, h, densityToCount(density, 5, 30), rng); break;
    case 'bubbles': drawBubbles(ctx, w, h, densityToCount(density, 8, 50), rng); break;
    case 'branches': drawBranches(ctx, w, h, densityToCount(density, 2, 10), rng); break;
    case 'ribbon': drawRibbon(ctx, w, h, densityToCount(density, 3, 15), rng); break;
    case 'static': drawStatic(ctx, w, h, densityToCount(density, 100, 800), rng); break;
    case 'dna': drawDNA(ctx, w, h, densityToCount(density, 2, 10), rng); break;
    case 'radar': drawRadar(ctx, w, h, densityToCount(density, 3, 12), rng); break;
    case 'pulse': drawPulse(ctx, w, h, densityToCount(density, 4, 20), rng); break;
    case 'shatter': drawShatter(ctx, w, h, densityToCount(density, 8, 40), rng); break;
    case 'weave': drawWeave(ctx, w, h, densityToCount(density, 5, 25), rng); break;
    case 'feather': drawFeather(ctx, w, h, densityToCount(density, 3, 15), rng); break;
    case 'ripple': drawRipple(ctx, w, h, densityToCount(density, 3, 15), rng); break;
    case 'glitch': drawGlitch(ctx, w, h, densityToCount(density, 8, 40), rng); break;
    case 'meteor': drawMeteor(ctx, w, h, densityToCount(density, 5, 30), rng); break;
    case 'terrain': drawTerrain(ctx, w, h, densityToCount(density, 3, 12), rng); break;
    case 'lattice': drawLattice(ctx, w, h, densityToCount(density, 4, 20), rng); break;
    case 'crescents': drawCrescents(ctx, w, h, densityToCount(density, 5, 30), rng); break;
    case 'prism': drawPrism(ctx, w, h, densityToCount(density, 5, 30), rng); break;
    case 'vortex': drawVortex(ctx, w, h, densityToCount(density, 3, 15), rng); break;
    case 'bamboo': drawBamboo(ctx, w, h, densityToCount(density, 4, 20), rng); break;
    case 'thorns': drawThorns(ctx, w, h, densityToCount(density, 5, 25), rng); break;
    case 'aurora': drawAurora(ctx, w, h, densityToCount(density, 4, 16), rng); break;
    case 'mosaic': drawMosaic(ctx, w, h, densityToCount(density, 15, 80), rng); break;
    case 'barcode': drawBarcode(ctx, w, h, densityToCount(density, 20, 100), rng); break;
    case 'contour': drawContour(ctx, w, h, densityToCount(density, 5, 30), rng); break;
    case 'cityscape': drawCityscape(ctx, w, h, densityToCount(density, 8, 40), rng); break;
    case 'frequency': drawFrequency(ctx, w, h, densityToCount(density, 4, 20), rng); break;
    case 'particles': drawParticles(ctx, w, h, densityToCount(density, 20, 120), rng); break;
  }
  ctx.restore();
}

function drawFlowField(ctx, w, h, count, rng) {
  const freq1 = 0.002 + rng() * 0.003;
  const freq2 = 0.004 + rng() * 0.004;
  const phase1 = rng() * 100;
  const phase2 = rng() * 100;
  function fieldAngle(x, y) {
    return Math.sin(x * freq1 + phase1) * Math.cos(y * freq1 + phase2) * Math.PI
      + Math.sin(x * freq2 + y * freq2 + phase1) * Math.PI * 0.5;
  }
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    ctx.beginPath();
    let x = rng() * w;
    let y = rng() * h;
    ctx.moveTo(x, y);
    const steps = 60 + Math.floor(rng() * 80);
    const stepLen = Math.max(w, h) * 0.008;
    ctx.lineWidth = lineW * (0.5 + rng() * 2);
    for (let s = 0; s < steps; s++) {
      const angle = fieldAngle(x, y);
      x += Math.cos(angle) * stepLen;
      y += Math.sin(angle) * stepLen;
      ctx.lineTo(x, y);
      if (x < -50 || x > w + 50 || y < -50 || y > h + 50) break;
    }
    ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawBrokenGrid(ctx, w, h, count, rng) {
  const margin = w * 0.06;
  const lineW = ctx.lineWidth;
  const cols = 6 + Math.floor(rng() * 6);
  const rows = Math.floor(cols * (h / w));
  const cellW = (w - margin * 2) / cols;
  const cellH = (h - margin * 2) / rows;

  for (let i = 0; i < count; i++) {
    const isHoriz = rng() > 0.35;
    ctx.lineWidth = lineW * (0.5 + rng() * 2.5);
    if (isHoriz) {
      const row = Math.floor(rng() * rows);
      const y = margin + row * cellH + (rng() - 0.5) * cellH * 0.3;
      const startCol = Math.floor(rng() * (cols - 1));
      const span = 1 + Math.floor(rng() * (cols - startCol));
      const x1 = margin + startCol * cellW;
      const x2 = margin + (startCol + span) * cellW;
      ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke();
    } else {
      const col = Math.floor(rng() * cols);
      const x = margin + col * cellW + (rng() - 0.5) * cellW * 0.3;
      const startRow = Math.floor(rng() * (rows - 1));
      const span = 1 + Math.floor(rng() * (rows - startRow));
      const y1 = margin + startRow * cellH;
      const y2 = margin + (startRow + span) * cellH;
      ctx.beginPath(); ctx.moveTo(x, y1); ctx.lineTo(x, y2); ctx.stroke();
    }
  }
  for (let i = 0; i < Math.floor(count * 0.6); i++) {
    const col = Math.floor(rng() * (cols + 1));
    const row = Math.floor(rng() * (rows + 1));
    const x = margin + col * cellW;
    const y = margin + row * cellH;
    const sz = w * 0.005 + rng() * w * 0.004;
    ctx.lineWidth = lineW;
    ctx.beginPath();
    const dir = Math.floor(rng() * 4);
    const dx = (dir === 0 || dir === 3) ? 1 : -1;
    const dy = (dir === 0 || dir === 1) ? 1 : -1;
    ctx.moveTo(x + dx * sz, y);
    ctx.lineTo(x, y);
    ctx.lineTo(x, y + dy * sz);
    ctx.stroke();
  }
  for (let i = 0; i < Math.floor(count * 0.2); i++) {
    const col = Math.floor(rng() * cols);
    const row = Math.floor(rng() * rows);
    const bx = margin + col * cellW + cellW * 0.1;
    const by = margin + row * cellH + cellH * 0.1;
    const bw = cellW * (0.15 + rng() * 0.3);
    const bh = cellH * (0.15 + rng() * 0.3);
    ctx.fillRect(bx, by, bw, bh);
  }
  ctx.lineWidth = lineW;
}

function drawOrbitalRings(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const systems = 3 + Math.floor(rng() * 3);
  for (let s = 0; s < systems; s++) {
    const cx = w * (0.15 + rng() * 0.7);
    const cy = h * (0.15 + rng() * 0.7);
    const ringsInSystem = 2 + Math.floor(rng() * 5);
    const tilt = (rng() - 0.5) * 0.6;
    const baseR = w * (0.04 + rng() * 0.08);
    for (let r = 0; r < ringsInSystem; r++) {
      const radius = baseR + r * (w * (0.02 + rng() * 0.03));
      const startAngle = rng() * Math.PI * 2;
      const sweep = Math.PI * (0.8 + rng() * 1.4);
      ctx.lineWidth = lineW * (0.3 + rng() * 2);
      ctx.beginPath();
      const steps = 60;
      for (let i = 0; i <= steps; i++) {
        const a = startAngle + (i / steps) * sweep;
        const rx = radius;
        const ry = radius * (0.4 + Math.abs(tilt));
        const x = cx + Math.cos(a) * rx;
        const y = cy + Math.sin(a) * ry;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    const dotCount = 2 + Math.floor(rng() * 4);
    for (let d = 0; d < dotCount; d++) {
      const a = rng() * Math.PI * 2;
      const orbitR = baseR + rng() * ringsInSystem * w * 0.03;
      const dx = cx + Math.cos(a) * orbitR;
      const dy = cy + Math.sin(a) * orbitR * (0.4 + Math.abs(tilt));
      const dotR = w * (0.002 + rng() * 0.004);
      ctx.beginPath();
      ctx.arc(dx, dy, dotR, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  const extras = Math.max(0, count - systems * 4);
  for (let i = 0; i < extras; i++) {
    const cx = rng() * w;
    const cy = rng() * h;
    const r = w * (0.02 + rng() * 0.1);
    const startAngle = rng() * Math.PI * 2;
    const sweep = Math.PI * (0.4 + rng() * 1.0);
    ctx.lineWidth = lineW * (0.3 + rng() * 1.5);
    ctx.beginPath(); ctx.arc(cx, cy, r, startAngle, startAngle + sweep); ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawTopographic(ctx, w, h, count, rng) {
  const cx = w * (0.3 + rng() * 0.4);
  const cy = h * (0.3 + rng() * 0.4);
  for (let i = 0; i < count; i++) {
    const baseR = (i + 1) * (Math.min(w, h) * 0.04);
    const pts = 80;
    const wobbles = [];
    for (let p = 0; p < pts; p++) {
      wobbles.push(1 + (rng() - 0.5) * 0.15);
    }
    const blend = 6;
    for (let b = 0; b < blend; b++) {
      const t = (b + 1) / (blend + 1);
      const idx = pts - blend + b;
      wobbles[idx] = wobbles[idx] * (1 - t) + wobbles[0] * t;
    }
    ctx.beginPath();
    for (let p = 0; p <= pts; p++) {
      const a = (p / pts) * Math.PI * 2;
      const w_val = wobbles[p % pts];
      const r = baseR * w_val;
      const x = cx + Math.cos(a) * r;
      const y = cy + Math.sin(a) * r;
      if (p === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
  }
}

function drawSpectrograph(ctx, w, h, count, rng) {
  const margin = w * 0.04;
  const baselineY = h + w * 0.01;
  const maxPeakH = h * 0.45;
  const barCount = Math.max(20, count * 3);
  const gap = Math.max(1, w * 0.001);
  const totalW = w - margin * 2;
  const barW = Math.max(2, (totalW - gap * barCount) / barCount);

  for (let i = 0; i < barCount; i++) {
    const linearT = i / barCount;
    const t = Math.pow(linearT, 0.6);
    let mag = 0.15
      + 0.55 * Math.exp(-t * 2.5)
      + 0.30 * Math.exp(-Math.pow((t - 0.15) * 3, 2))
      + 0.25 * Math.exp(-Math.pow((t - 0.35) * 4, 2))
      + 0.20 * Math.exp(-Math.pow((t - 0.6) * 5, 2))
      + (rng() - 0.35) * 0.35;
    if (linearT < 0.1) {
      mag *= 0.7 + linearT * 3;
    }
    mag = Math.max(0.03, Math.min(1, mag));
    const peakH = mag * maxPeakH;
    const x = margin + i * (barW + gap);
    ctx.fillRect(x, baselineY - peakH, barW, peakH);
    const capH = Math.max(1, barW * 0.4);
    const capGap = 2 + rng() * 4;
    ctx.fillRect(x, baselineY - peakH - capH - capGap, barW, capH);
  }
}

function drawWaveform(ctx, w, h, count, rng) {
  const margin = w * 0.08;
  const usableW = w - margin * 2;
  const segments = 128;

  for (let i = 0; i < count; i++) {
    const baseY = margin + (i + 1) * (h - margin * 2) / (count + 1);
    const maxAmp = h * (0.015 + rng() * 0.045);
    const f1 = 2 + rng() * 6;
    const f2 = 6 + rng() * 12;
    const f3 = 12 + rng() * 20;
    const p1 = rng() * Math.PI * 2;
    const p2 = rng() * Math.PI * 2;
    const p3 = rng() * Math.PI * 2;
    const noiseScale = 0.15 + rng() * 0.2;

    const amps = [];
    for (let s = 0; s <= segments; s++) {
      const t = s / segments;
      let a = 0.25
        + 0.30 * Math.abs(Math.sin(t * Math.PI * f1 + p1))
        + 0.25 * Math.abs(Math.sin(t * Math.PI * f2 + p2))
        + 0.20 * Math.abs(Math.sin(t * Math.PI * f3 + p3));
      a += (rng() - 0.5) * noiseScale;
      const edge = Math.min(t * 6, (1 - t) * 6, 1);
      amps.push(Math.max(0, a * edge * maxAmp));
    }

    ctx.beginPath();
    for (let s = 0; s <= segments; s++) {
      const x = margin + (s / segments) * usableW;
      if (s === 0) ctx.moveTo(x, baseY - amps[s]);
      else ctx.lineTo(x, baseY - amps[s]);
    }
    for (let s = segments; s >= 0; s--) {
      ctx.lineTo(margin + (s / segments) * usableW, baseY + amps[s]);
    }
    ctx.closePath();
    ctx.fill();
  }
}

function drawFire(ctx, w, h, count, rng) {
  const baseY = h * 0.85;
  const flameCount = Math.max(3, Math.floor(count * 0.6));
  for (let i = 0; i < flameCount; i++) {
    const cx = w * (0.1 + rng() * 0.8);
    const flameH = h * (0.15 + rng() * 0.40);
    const flameW = w * (0.02 + rng() * 0.06);
    const lean = (rng() - 0.5) * flameW * 1.5;
    ctx.beginPath();
    ctx.moveTo(cx - flameW, baseY);
    const segments = 16;
    for (let s = 1; s <= segments; s++) {
      const t = s / segments;
      const taper = 1 - Math.pow(t, 0.6);
      const wobble = Math.sin(t * Math.PI * (3 + rng() * 4)) * flameW * 0.3 * taper;
      const x = cx - flameW * taper + lean * t + wobble;
      const y = baseY - flameH * t;
      ctx.lineTo(x, y);
    }
    ctx.lineTo(cx + lean, baseY - flameH);
    for (let s = segments; s >= 1; s--) {
      const t = s / segments;
      const taper = 1 - Math.pow(t, 0.6);
      const wobble = Math.sin(t * Math.PI * (2 + rng() * 5) + 1.5) * flameW * 0.3 * taper;
      const x = cx + flameW * taper + lean * t + wobble;
      const y = baseY - flameH * t;
      ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
  }
  const innerCount = Math.max(2, Math.floor(count * 0.3));
  for (let i = 0; i < innerCount; i++) {
    const cx = w * (0.2 + rng() * 0.6);
    const flameH = h * (0.08 + rng() * 0.20);
    const flameW = w * (0.01 + rng() * 0.025);
    const lean = (rng() - 0.5) * flameW;
    ctx.beginPath();
    ctx.moveTo(cx - flameW, baseY);
    ctx.quadraticCurveTo(cx - flameW * 0.5 + lean * 0.5, baseY - flameH * 0.6, cx + lean, baseY - flameH);
    ctx.quadraticCurveTo(cx + flameW * 0.5 + lean * 0.5, baseY - flameH * 0.6, cx + flameW, baseY);
    ctx.closePath();
    ctx.fill();
  }
  const emberCount = Math.floor(count * 0.5);
  for (let i = 0; i < emberCount; i++) {
    const ex = w * (0.15 + rng() * 0.7);
    const ey = baseY - h * (0.3 + rng() * 0.45);
    const er = w * (0.001 + rng() * 0.003);
    ctx.beginPath();
    ctx.arc(ex, ey, er, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawOscillator(ctx, w, h, count, rng) {
  const cx = w / 2;
  const cy = h / 2;
  const lineW = ctx.lineWidth;
  const beamW = w * 0.015;
  const beamH = h * 0.7;
  const beamTop = cy - beamH / 2;
  const pixelSize = Math.max(2, w * 0.004);
  const beamCols = Math.ceil(beamW / pixelSize);
  const beamRows = Math.ceil(beamH / pixelSize);

  for (let row = 0; row < beamRows; row++) {
    for (let col = 0; col < beamCols; col++) {
      if (rng() > 0.25) {
        const px = cx - beamW / 2 + col * pixelSize;
        const py = beamTop + row * pixelSize;
        ctx.fillRect(px, py, pixelSize - 1, pixelSize - 1);
      }
    }
  }
  const trailCount = Math.max(8, Math.floor(count * 0.8));
  for (let i = 0; i < trailCount; i++) {
    const baseAngle = rng() * Math.PI * 2;
    const angle = baseAngle;
    const maxR = Math.min(w, h) * (0.15 + rng() * 0.30);
    const angleFactor = 0.3 + 0.7 * Math.abs(Math.cos(angle));
    const trailR = maxR * angleFactor;
    const particles = 6 + Math.floor(rng() * 12);
    const startR = w * 0.02;
    for (let p = 0; p < particles; p++) {
      const t = (p + 0.5) / particles;
      const r = startR + t * trailR;
      const spiralOffset = t * (rng() - 0.5) * 0.4;
      const px = cx + Math.cos(angle + spiralOffset) * r;
      const py = cy + Math.sin(angle + spiralOffset) * r;
      const pSize = pixelSize * (1.5 - t * 0.8);
      const jx = (rng() - 0.5) * pixelSize * 2;
      const jy = (rng() - 0.5) * pixelSize * 2;
      ctx.fillRect(px + jx - pSize / 2, py + jy - pSize / 2, pSize, pSize);
    }
  }
  const ringCount = 3 + Math.floor(rng() * 4);
  for (let i = 0; i < ringCount; i++) {
    const r = w * (0.06 + rng() * 0.18);
    const startA = rng() * Math.PI * 2;
    const sweep = Math.PI * (0.4 + rng() * 1.0);
    ctx.lineWidth = lineW * (0.5 + rng() * 1.5);
    ctx.beginPath();
    const steps = 40;
    for (let s = 0; s <= steps; s++) {
      const a = startA + (s / steps) * sweep;
      const x = cx + Math.cos(a) * r * 1.3;
      const y = cy + Math.sin(a) * r * 0.6;
      if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  const scatterCount = Math.floor(count * 0.5);
  for (let i = 0; i < scatterCount; i++) {
    const dist = rng() * w * 0.08;
    const a = rng() * Math.PI * 2;
    const sx = cx + Math.cos(a) * dist * 1.2;
    const sy = cy + Math.sin(a) * dist * 0.5;
    const sz = pixelSize * (0.5 + rng() * 0.8);
    if (rng() > 0.3) {
      ctx.fillRect(sx - sz / 2, sy - sz / 2, sz, sz);
    }
  }
  ctx.lineWidth = lineW;
}

function drawSmiley(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const cx = w * (0.05 + rng() * 0.9);
    const cy = h * (0.05 + rng() * 0.9);
    const r = w * (0.02 + rng() * 0.06);
    const rotation = (rng() - 0.5) * 0.6;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotation);
    ctx.lineWidth = Math.max(1.5, r * 0.08);
    ctx.lineCap = 'round';
    // Face circle
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.stroke();
    // X eyes
    const eyeY = -r * 0.18;
    const eyeX = r * 0.32;
    const xSize = r * 0.14;
    ctx.lineWidth = Math.max(1.5, r * 0.07);
    ctx.beginPath();
    ctx.moveTo(-eyeX - xSize, eyeY - xSize);
    ctx.lineTo(-eyeX + xSize, eyeY + xSize);
    ctx.moveTo(-eyeX + xSize, eyeY - xSize);
    ctx.lineTo(-eyeX - xSize, eyeY + xSize);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(eyeX - xSize, eyeY - xSize);
    ctx.lineTo(eyeX + xSize, eyeY + xSize);
    ctx.moveTo(eyeX + xSize, eyeY - xSize);
    ctx.lineTo(eyeX - xSize, eyeY + xSize);
    ctx.stroke();
    // Wavy mouth
    ctx.lineWidth = Math.max(1.5, r * 0.07);
    ctx.beginPath();
    const smileW = r * 0.55;
    const smileY = r * 0.25;
    const segs = 8;
    const amp = r * 0.06;
    for (let s = 0; s <= segs; s++) {
      const t = s / segs;
      const sx = -smileW + t * smileW * 2;
      const sy = smileY + Math.sin(t * Math.PI * 3 + rng() * 2) * amp;
      if (s === 0) ctx.moveTo(sx, sy); else ctx.lineTo(sx, sy);
    }
    ctx.stroke();
    ctx.restore();
  }
  ctx.lineWidth = lineW;
}

function drawConstellation(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const stars = [];
  for (let i = 0; i < count; i++) {
    stars.push({ x: rng() * w, y: rng() * h, r: w * (0.002 + rng() * 0.005) });
  }
  // Connect nearby stars with thin lines
  const maxDist = Math.min(w, h) * (0.12 + rng() * 0.10);
  ctx.lineWidth = lineW * 0.4;
  for (let i = 0; i < stars.length; i++) {
    for (let j = i + 1; j < stars.length; j++) {
      const dx = stars[j].x - stars[i].x;
      const dy = stars[j].y - stars[i].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < maxDist) {
        const alpha = 1 - dist / maxDist;
        ctx.globalAlpha *= alpha * 0.8;
        ctx.beginPath();
        ctx.moveTo(stars[i].x, stars[i].y);
        ctx.lineTo(stars[j].x, stars[j].y);
        ctx.stroke();
        ctx.globalAlpha /= alpha * 0.8;
      }
    }
  }
  ctx.lineWidth = lineW;
  // Draw star dots
  for (const s of stars) {
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawCircuit(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const padR = w * 0.008;
  ctx.lineWidth = lineW * 1.2;
  for (let i = 0; i < count; i++) {
    let x = rng() * w;
    let y = rng() * h;
    const segments = 3 + Math.floor(rng() * 6);
    ctx.beginPath();
    ctx.moveTo(x, y);
    for (let s = 0; s < segments; s++) {
      // Right-angle traces: alternate horizontal then vertical
      if (s % 2 === 0) {
        x += (rng() > 0.5 ? 1 : -1) * w * (0.04 + rng() * 0.12);
      } else {
        y += (rng() > 0.5 ? 1 : -1) * h * (0.04 + rng() * 0.12);
      }
      ctx.lineTo(x, y);
    }
    ctx.stroke();
    // Draw pad at end
    ctx.beginPath();
    ctx.arc(x, y, padR + rng() * padR, 0, Math.PI * 2);
    ctx.stroke();
  }
  // Scatter via-pads
  for (let i = 0; i < count * 2; i++) {
    const px = rng() * w;
    const py = rng() * h;
    const r = padR * (0.5 + rng() * 1.0);
    ctx.beginPath();
    ctx.arc(px, py, r, 0, Math.PI * 2);
    if (rng() > 0.5) ctx.fill(); else ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawChevron(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const rowH = h / (count + 1);
  const chevW = w * (0.06 + rng() * 0.06);
  const chevH = rowH * (0.25 + rng() * 0.25);
  const cols = Math.floor(w / (chevW * 2)) + 2;
  ctx.lineWidth = lineW * (0.8 + rng() * 1.2);
  ctx.lineJoin = 'miter';
  for (let row = 0; row < count; row++) {
    const cy = rowH * (row + 1);
    const offset = (row % 2 === 0) ? 0 : chevW;
    for (let col = -1; col < cols; col++) {
      const cx = col * chevW * 2 + offset;
      ctx.beginPath();
      ctx.moveTo(cx, cy + chevH);
      ctx.lineTo(cx + chevW, cy - chevH);
      ctx.lineTo(cx + chevW * 2, cy + chevH);
      ctx.stroke();
    }
  }
  ctx.lineWidth = lineW;
}

function drawDiamonds(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const cx = rng() * w;
    const cy = rng() * h;
    const rw = w * (0.02 + rng() * 0.07);
    const rh = rw * (0.5 + rng() * 1.0);
    const angle = (rng() - 0.5) * Math.PI * 0.25;
    ctx.lineWidth = lineW * (0.5 + rng() * 1.5);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(0, -rh);
    ctx.lineTo(rw, 0);
    ctx.lineTo(0, rh);
    ctx.lineTo(-rw, 0);
    ctx.closePath();
    if (rng() > 0.65) ctx.fill(); else ctx.stroke();
    // Optional inner diamond
    if (rng() > 0.5) {
      ctx.beginPath();
      ctx.moveTo(0, -rh * 0.5);
      ctx.lineTo(rw * 0.5, 0);
      ctx.lineTo(0, rh * 0.5);
      ctx.lineTo(-rw * 0.5, 0);
      ctx.closePath();
      ctx.stroke();
    }
    ctx.restore();
  }
  ctx.lineWidth = lineW;
}

function drawCrosshatch(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const groups = 2 + Math.floor(rng() * 3);
  const angles = [];
  const baseAngle = rng() * Math.PI * 0.5;
  for (let g = 0; g < groups; g++) {
    angles.push(baseAngle + g * (Math.PI / groups));
  }
  for (let g = 0; g < groups; g++) {
    const angle = angles[g];
    const spacing = Math.min(w, h) / (count * 0.8);
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);
    const diag = Math.sqrt(w * w + h * h);
    const numLines = Math.floor(diag / spacing);
    ctx.lineWidth = lineW * (0.4 + rng() * 0.8);
    for (let i = -Math.floor(numLines / 2); i <= Math.floor(numLines / 2); i++) {
      const offset = i * spacing;
      const px = w / 2 + (-sinA) * offset;
      const py = h / 2 + cosA * offset;
      ctx.beginPath();
      ctx.moveTo(px - cosA * diag, py - sinA * diag);
      ctx.lineTo(px + cosA * diag, py + sinA * diag);
      ctx.stroke();
    }
  }
  ctx.lineWidth = lineW;
}

function drawDots(ctx, w, h, count, rng) {
  for (let i = 0; i < count; i++) {
    const x = rng() * w;
    const y = rng() * h;
    const r = w * (0.003 + Math.pow(rng(), 2) * 0.018);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawSpiral(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const cx = rng() * w;
    const cy = rng() * h;
    const maxR = Math.min(w, h) * (0.05 + rng() * 0.18);
    const turns = 2 + rng() * 5;
    const totalSteps = Math.floor(turns * 60);
    const spacing = maxR / (turns * 60);
    ctx.lineWidth = lineW * (0.5 + rng() * 1.5);
    ctx.beginPath();
    for (let s = 0; s <= totalSteps; s++) {
      const angle = (s / 60) * Math.PI * 2;
      const r = s * spacing;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawMaze(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const cellSize = Math.min(w, h) / (count + 2);
  const cols = Math.floor(w / cellSize);
  const rows = Math.floor(h / cellSize);
  ctx.lineWidth = lineW * 1.5;
  ctx.lineCap = 'square';
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = col * cellSize;
      const y = row * cellSize;
      // Randomly draw walls
      if (rng() > 0.45) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + cellSize, y);
        ctx.stroke();
      }
      if (rng() > 0.45) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x, y + cellSize);
        ctx.stroke();
      }
    }
  }
  // Right and bottom borders
  for (let row = 0; row <= rows; row++) {
    ctx.beginPath();
    ctx.moveTo(cols * cellSize, row * cellSize);
    ctx.lineTo(cols * cellSize, (row + 1) * cellSize);
    ctx.stroke();
  }
  for (let col = 0; col <= cols; col++) {
    ctx.beginPath();
    ctx.moveTo(col * cellSize, rows * cellSize);
    ctx.lineTo((col + 1) * cellSize, rows * cellSize);
    ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawLightning(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const startX = rng() * w;
    const startY = 0;
    let x = startX;
    let y = startY;
    const targetY = h * (0.5 + rng() * 0.5);
    const segments = 6 + Math.floor(rng() * 8);
    ctx.lineWidth = lineW * (1 + rng() * 3);
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(x, y);
    for (let s = 0; s < segments; s++) {
      const progress = (s + 1) / segments;
      y = startY + progress * targetY;
      x += (rng() - 0.5) * w * 0.15;
      // Occasional branch split
      if (rng() > 0.75 && s < segments - 1) {
        const bx = x;
        const by = y;
        const branchLen = 3 + Math.floor(rng() * 3);
        ctx.lineTo(x, y);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(bx, by);
        let bxc = bx;
        let byc = by;
        for (let b = 0; b < branchLen; b++) {
          bxc += (rng() - 0.5) * w * 0.1 + (rng() > 0.5 ? 1 : -1) * w * 0.04;
          byc += h * 0.04;
          ctx.lineTo(bxc, byc);
        }
        ctx.stroke();
        ctx.lineWidth = lineW * (1 + rng() * 3);
        ctx.beginPath();
        ctx.moveTo(x, y);
      }
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawRain(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const angle = (rng() - 0.5) * 0.3; // slight lean
  for (let i = 0; i < count; i++) {
    const x = rng() * w * 1.3 - w * 0.15;
    const y = rng() * h;
    const len = h * (0.02 + rng() * 0.06);
    ctx.lineWidth = lineW * (0.3 + rng() * 0.8);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.sin(angle) * len, y + Math.cos(angle) * len);
    ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawHexgrid(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const hexR = Math.min(w, h) / (count * 1.5 + 2);
  const hexW = hexR * 2;
  const hexH = Math.sqrt(3) * hexR;
  const cols = Math.ceil(w / (hexW * 0.75)) + 2;
  const rows = Math.ceil(h / hexH) + 2;
  ctx.lineWidth = lineW * 0.9;
  for (let row = -1; row < rows; row++) {
    for (let col = -1; col < cols; col++) {
      const cx = col * hexW * 0.75;
      const cy = row * hexH + (col % 2 === 0 ? 0 : hexH * 0.5);
      // Draw only a random subset of the 6 edges
      for (let edge = 0; edge < 6; edge++) {
        if (rng() > 0.35) {
          const a1 = (edge / 6) * Math.PI * 2 - Math.PI / 6;
          const a2 = ((edge + 1) / 6) * Math.PI * 2 - Math.PI / 6;
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(a1) * hexR, cy + Math.sin(a1) * hexR);
          ctx.lineTo(cx + Math.cos(a2) * hexR, cy + Math.sin(a2) * hexR);
          ctx.stroke();
        }
      }
    }
  }
  ctx.lineWidth = lineW;
}

function drawScatter(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const x = rng() * w;
    const y = rng() * h;
    const len = w * (0.01 + rng() * 0.04);
    const angle = rng() * Math.PI * 2;
    ctx.lineWidth = lineW * (0.4 + rng() * 1.2);
    ctx.beginPath();
    ctx.moveTo(x - Math.cos(angle) * len * 0.5, y - Math.sin(angle) * len * 0.5);
    ctx.lineTo(x + Math.cos(angle) * len * 0.5, y + Math.sin(angle) * len * 0.5);
    ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawWaves(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const margin = h * 0.05;
  const usableH = h - margin * 2;
  const steps = 120;
  for (let i = 0; i < count; i++) {
    const baseY = margin + (i / (count - 1 || 1)) * usableH;
    const amp = h * (0.01 + rng() * 0.03);
    const freq = 1 + rng() * 4;
    const phase = rng() * Math.PI * 2;
    const freq2 = freq * (1.5 + rng() * 2);
    const amp2 = amp * (0.3 + rng() * 0.4);
    ctx.lineWidth = lineW * (0.6 + rng() * 1.4);
    ctx.beginPath();
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const x = t * w;
      const y = baseY
        + Math.sin(t * Math.PI * 2 * freq + phase) * amp
        + Math.sin(t * Math.PI * 2 * freq2 + phase * 1.3) * amp2;
      if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawBubbles(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const cx = rng() * w;
    const cy = rng() * h;
    const r = w * (0.015 + Math.pow(rng(), 1.5) * 0.08);
    ctx.lineWidth = lineW * (0.4 + rng() * 1.2);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
    // Specular highlight arc
    if (rng() > 0.4) {
      const hiAngle = -Math.PI * 0.75 + rng() * 0.5;
      ctx.lineWidth = lineW * (0.3 + rng() * 0.5);
      ctx.beginPath();
      ctx.arc(cx - r * 0.2, cy - r * 0.2, r * 0.35, hiAngle, hiAngle + Math.PI * 0.6);
      ctx.stroke();
    }
  }
  ctx.lineWidth = lineW;
}

function drawBranches(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  function branch(x, y, angle, len, depth) {
    if (depth === 0 || len < 2) return;
    const ex = x + Math.cos(angle) * len;
    const ey = y + Math.sin(angle) * len;
    ctx.lineWidth = Math.max(0.5, lineW * (depth * 0.3));
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(ex, ey);
    ctx.stroke();
    const spread = 0.3 + rng() * 0.5;
    const branches = rng() > 0.3 ? 2 : 3;
    for (let b = 0; b < branches; b++) {
      const da = (b - (branches - 1) / 2) * spread + (rng() - 0.5) * 0.2;
      branch(ex, ey, angle + da, len * (0.6 + rng() * 0.2), depth - 1);
    }
  }
  for (let i = 0; i < count; i++) {
    const x = rng() * w;
    const y = h * (0.6 + rng() * 0.4);
    const startAngle = -Math.PI * 0.5 + (rng() - 0.5) * 0.4;
    const startLen = h * (0.08 + rng() * 0.12);
    branch(x, y, startAngle, startLen, 5);
  }
  ctx.lineWidth = lineW;
}

function drawRibbon(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const steps = 80;
    const freq = 1 + rng() * 2;
    const phase = rng() * Math.PI * 2;
    const ribbonW = h * (0.02 + rng() * 0.05);
    const amplitude = h * (0.05 + rng() * 0.15);
    const centerY = h * (0.1 + rng() * 0.8);
    ctx.lineWidth = lineW * 0.6;
    ctx.beginPath();
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const x = t * w;
      const twist = Math.sin(t * Math.PI * 2 * freq + phase);
      const y = centerY + twist * amplitude;
      const w2 = ribbonW * Math.cos(t * Math.PI * 2 * freq + phase + Math.PI * 0.5);
      if (s === 0) ctx.moveTo(x, y - Math.abs(w2));
      else ctx.lineTo(x, y - Math.abs(w2));
    }
    for (let s = steps; s >= 0; s--) {
      const t = s / steps;
      const x = t * w;
      const twist = Math.sin(t * Math.PI * 2 * freq + phase);
      const y = centerY + twist * amplitude;
      const w2 = ribbonW * Math.cos(t * Math.PI * 2 * freq + phase + Math.PI * 0.5);
      ctx.lineTo(x, y + Math.abs(w2));
    }
    ctx.closePath();
    ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawStatic(ctx, w, h, count, rng) {
  const pixSize = Math.max(2, w * 0.004);
  const cols = Math.ceil(w / pixSize);
  const rows = Math.ceil(h / pixSize);
  const threshold = 1 - count / 1000;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (rng() > threshold) {
        const brightness = rng();
        ctx.globalAlpha *= brightness;
        ctx.fillRect(col * pixSize, row * pixSize, pixSize - 1, pixSize - 1);
        ctx.globalAlpha /= brightness;
      }
    }
  }
}

function drawDNA(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const cx = w * (0.1 + rng() * 0.8);
    const helixH = h * (0.4 + rng() * 0.4);
    const topY = (h - helixH) * rng();
    const helixW = w * (0.03 + rng() * 0.06);
    const turns = 2 + Math.floor(rng() * 4);
    const steps = turns * 20;
    // Strand 1
    ctx.lineWidth = lineW * 0.8;
    ctx.beginPath();
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const x = cx + Math.cos(t * Math.PI * 2 * turns) * helixW;
      const y = topY + t * helixH;
      if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
    // Strand 2 (offset by PI)
    ctx.beginPath();
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const x = cx + Math.cos(t * Math.PI * 2 * turns + Math.PI) * helixW;
      const y = topY + t * helixH;
      if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
    // Rungs connecting strands
    ctx.lineWidth = lineW * 0.4;
    const rungCount = turns * 4;
    for (let r = 0; r < rungCount; r++) {
      const t = (r + 0.5) / rungCount;
      const x1 = cx + Math.cos(t * Math.PI * 2 * turns) * helixW;
      const x2 = cx + Math.cos(t * Math.PI * 2 * turns + Math.PI) * helixW;
      const y = topY + t * helixH;
      ctx.beginPath();
      ctx.moveTo(x1, y);
      ctx.lineTo(x2, y);
      ctx.stroke();
    }
  }
  ctx.lineWidth = lineW;
}

function drawRadar(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const cx = w * (0.2 + rng() * 0.6);
  const cy = h * (0.2 + rng() * 0.6);
  const maxR = Math.min(w, h) * (0.25 + rng() * 0.2);
  // Concentric rings
  ctx.lineWidth = lineW * 0.6;
  for (let i = 1; i <= count; i++) {
    const r = (i / count) * maxR;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  }
  // Radial spokes
  const spokeCount = 6 + Math.floor(rng() * 6);
  ctx.lineWidth = lineW * 0.5;
  for (let s = 0; s < spokeCount; s++) {
    const angle = (s / spokeCount) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(angle) * maxR, cy + Math.sin(angle) * maxR);
    ctx.stroke();
  }
  // Sweep arc (partial bright arc)
  const sweepAngle = rng() * Math.PI * 2;
  ctx.lineWidth = lineW * 1.5;
  ctx.beginPath();
  ctx.arc(cx, cy, maxR * (0.6 + rng() * 0.35), sweepAngle, sweepAngle + Math.PI * 0.2);
  ctx.stroke();
  // Blips
  const blipCount = 3 + Math.floor(rng() * 5);
  for (let b = 0; b < blipCount; b++) {
    const ba = rng() * Math.PI * 2;
    const br = rng() * maxR;
    ctx.beginPath();
    ctx.arc(cx + Math.cos(ba) * br, cy + Math.sin(ba) * br, w * 0.004, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.lineWidth = lineW;
}

function drawPulse(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const margin = h * 0.06;
  const usableH = h - margin * 2;
  for (let i = 0; i < count; i++) {
    const baseY = margin + (i / (count - 1 || 1)) * usableH;
    const steps = 200;
    ctx.lineWidth = lineW * (0.5 + rng() * 1.2);
    ctx.beginPath();
    let x = 0;
    ctx.moveTo(x, baseY);
    while (x < w) {
      // Flat segment
      const flatLen = w * (0.05 + rng() * 0.1);
      ctx.lineTo(Math.min(x + flatLen, w), baseY);
      x += flatLen;
      if (x >= w) break;
      // Pulse spike
      const spikeH = h * (0.02 + rng() * 0.06) * (rng() > 0.5 ? 1 : -1);
      const spikeW = w * (0.015 + rng() * 0.03);
      ctx.lineTo(x + spikeW * 0.25, baseY - spikeH * 0.1);
      ctx.lineTo(x + spikeW * 0.4, baseY - spikeH);
      ctx.lineTo(x + spikeW * 0.6, baseY + spikeH * 0.5);
      ctx.lineTo(x + spikeW * 0.75, baseY + spikeH * 0.2);
      ctx.lineTo(x + spikeW, baseY);
      x += spikeW;
    }
    ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawShatter(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  // Generate random points and connect them as polygon fragments
  const points = [];
  for (let i = 0; i < count; i++) {
    points.push({ x: rng() * w, y: rng() * h });
  }
  // Add corners
  points.push({ x: 0, y: 0 }, { x: w, y: 0 }, { x: 0, y: h }, { x: w, y: h });
  ctx.lineWidth = lineW * 0.8;
  // Draw edges between nearby points to simulate shattered glass
  const maxEdge = Math.min(w, h) * (0.15 + rng() * 0.15);
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const dx = points[j].x - points[i].x;
      const dy = points[j].y - points[i].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < maxEdge && rng() > 0.3) {
        ctx.beginPath();
        ctx.moveTo(points[i].x, points[i].y);
        ctx.lineTo(points[j].x, points[j].y);
        ctx.stroke();
      }
    }
  }
  // Draw small dots at shard vertices
  for (const p of points) {
    if (rng() > 0.5) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, w * 0.003, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.lineWidth = lineW;
}

function drawWeave(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const cellSize = Math.min(w, h) / (count + 1);
  const cols = Math.ceil(w / cellSize) + 1;
  const rows = Math.ceil(h / cellSize) + 1;
  ctx.lineWidth = lineW * 1.2;
  ctx.lineCap = 'round';
  // Horizontal strands
  for (let row = 0; row < rows; row++) {
    const y = row * cellSize;
    for (let col = 0; col < cols; col++) {
      const x = col * cellSize;
      const over = (row + col) % 2 === 0;
      if (over) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + cellSize, y);
        ctx.stroke();
      }
    }
  }
  // Vertical strands
  for (let col = 0; col < cols; col++) {
    const x = col * cellSize;
    for (let row = 0; row < rows; row++) {
      const y = row * cellSize;
      const over = (row + col) % 2 !== 0;
      if (over) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x, y + cellSize);
        ctx.stroke();
      }
    }
  }
  ctx.lineWidth = lineW;
}

function drawFeather(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const cx = rng() * w;
    const cy = rng() * h;
    const angle = rng() * Math.PI * 2;
    const len = Math.min(w, h) * (0.06 + rng() * 0.14);
    const barbCount = 8 + Math.floor(rng() * 10);
    // Central quill
    ctx.lineWidth = lineW * 1.2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(angle) * len, cy + Math.sin(angle) * len);
    ctx.stroke();
    // Barbs
    ctx.lineWidth = lineW * 0.4;
    for (let b = 0; b < barbCount; b++) {
      const t = (b + 1) / (barbCount + 1);
      const bx = cx + Math.cos(angle) * len * t;
      const by = cy + Math.sin(angle) * len * t;
      const barbLen = len * 0.35 * (1 - Math.abs(t - 0.5) * 1.5);
      const spread = Math.PI * 0.35;
      for (const side of [-1, 1]) {
        const ba = angle + Math.PI * 0.5 + side * spread * (0.6 + rng() * 0.4);
        ctx.beginPath();
        ctx.moveTo(bx, by);
        ctx.quadraticCurveTo(
          bx + Math.cos(angle + side * spread * 0.5) * barbLen * 0.5,
          by + Math.sin(angle + side * spread * 0.5) * barbLen * 0.5,
          bx + Math.cos(ba) * barbLen,
          by + Math.sin(ba) * barbLen
        );
        ctx.stroke();
      }
    }
  }
  ctx.lineWidth = lineW;
}

function drawRipple(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const centers = [];
  const numCenters = 2 + Math.floor(rng() * 4);
  for (let c = 0; c < numCenters; c++) {
    centers.push({ x: rng() * w, y: rng() * h });
  }
  const ringsPerCenter = Math.floor(count / numCenters);
  for (const ctr of centers) {
    const maxR = Math.min(w, h) * (0.1 + rng() * 0.25);
    for (let r = 1; r <= ringsPerCenter; r++) {
      const radius = (r / ringsPerCenter) * maxR;
      ctx.lineWidth = lineW * (1.2 - r / ringsPerCenter * 0.8);
      ctx.beginPath();
      ctx.arc(ctr.x, ctr.y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.lineWidth = lineW;
}

function drawGlitch(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const y = rng() * h;
    const barH = h * (0.005 + rng() * 0.02);
    const xOffset = (rng() - 0.5) * w * 0.15;
    const barW = w * (0.2 + rng() * 0.8);
    const barX = rng() * (w - barW);
    // Shifted horizontal bar
    ctx.fillRect(barX + xOffset, y, barW, barH);
    // Scan line effect
    if (rng() > 0.5) {
      ctx.lineWidth = lineW * 0.4;
      ctx.beginPath();
      ctx.moveTo(0, y + barH * 2);
      ctx.lineTo(w, y + barH * 2);
      ctx.stroke();
    }
    // Digital noise blocks
    if (rng() > 0.6) {
      const nx = rng() * w;
      const nw = w * (0.01 + rng() * 0.04);
      const nh = h * (0.005 + rng() * 0.015);
      ctx.fillRect(nx, y - nh, nw, nh);
    }
  }
  ctx.lineWidth = lineW;
}

function drawMeteor(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const angle = Math.PI * 0.25 + (rng() - 0.5) * 0.5;
    const hx = rng() * w;
    const hy = rng() * h * 0.6;
    const tailLen = Math.min(w, h) * (0.06 + rng() * 0.18);
    const tx = hx - Math.cos(angle) * tailLen;
    const ty = hy - Math.sin(angle) * tailLen;
    // Tail (thin, fading)
    ctx.lineWidth = lineW * 0.3;
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(hx, hy);
    ctx.stroke();
    // Head dot
    ctx.lineWidth = lineW;
    ctx.beginPath();
    ctx.arc(hx, hy, w * (0.003 + rng() * 0.005), 0, Math.PI * 2);
    ctx.fill();
    // Glow ring
    if (rng() > 0.4) {
      ctx.lineWidth = lineW * 0.3;
      ctx.beginPath();
      ctx.arc(hx, hy, w * 0.01, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.lineWidth = lineW;
}

function drawTerrain(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const baseY = h * (0.3 + (i / count) * 0.5);
    const amplitude = h * (0.05 + rng() * 0.12);
    const steps = 80;
    const freq1 = 1 + rng() * 2;
    const freq2 = 3 + rng() * 5;
    const phase1 = rng() * Math.PI * 2;
    const phase2 = rng() * Math.PI * 2;
    ctx.lineWidth = lineW * (0.6 + rng() * 1.0);
    ctx.beginPath();
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const x = t * w;
      const y = baseY
        - Math.abs(Math.sin(t * Math.PI * freq1 + phase1)) * amplitude
        - Math.abs(Math.sin(t * Math.PI * freq2 + phase2)) * amplitude * 0.4;
      if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    // Close ridge silhouette to bottom
    ctx.lineTo(w, h);
    ctx.lineTo(0, h);
    ctx.closePath();
    ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawLattice(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const spacing = Math.min(w, h) / (count + 1);
  const angle1 = Math.PI * 0.25 + (rng() - 0.5) * 0.3;
  const angle2 = Math.PI * 0.75 + (rng() - 0.5) * 0.3;
  const diag = Math.sqrt(w * w + h * h);
  ctx.lineWidth = lineW * 0.7;
  for (const angle of [angle1, angle2]) {
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);
    const numLines = Math.floor(diag / spacing) + 2;
    for (let i = -Math.floor(numLines / 2); i <= Math.floor(numLines / 2); i++) {
      const offset = i * spacing;
      const px = w / 2 + (-sinA) * offset;
      const py = h / 2 + cosA * offset;
      ctx.beginPath();
      ctx.moveTo(px - cosA * diag, py - sinA * diag);
      ctx.lineTo(px + cosA * diag, py + sinA * diag);
      ctx.stroke();
    }
  }
  // Draw dots at intersections
  const dotSpacing = spacing;
  for (let i = -20; i <= 20; i++) {
    for (let j = -20; j <= 20; j++) {
      const x = w / 2 + i * dotSpacing * Math.cos(angle1) + j * dotSpacing * Math.cos(angle2);
      const y = h / 2 + i * dotSpacing * Math.sin(angle1) + j * dotSpacing * Math.sin(angle2);
      if (x >= 0 && x <= w && y >= 0 && y <= h && rng() > 0.5) {
        ctx.beginPath();
        ctx.arc(x, y, w * 0.003, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  ctx.lineWidth = lineW;
}

function drawCrescents(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const cx = rng() * w;
    const cy = rng() * h;
    const r = w * (0.015 + rng() * 0.05);
    const rotation = rng() * Math.PI * 2;
    ctx.lineWidth = lineW * (0.5 + rng() * 1.2);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotation);
    // Outer arc (full circle)
    ctx.beginPath();
    ctx.arc(0, 0, r, -Math.PI * 0.7, Math.PI * 0.7);
    ctx.stroke();
    // Inner arc offset to create crescent
    const offset = r * 0.45;
    ctx.beginPath();
    ctx.arc(offset, 0, r * 0.75, Math.PI * 0.3, -Math.PI * 0.3, true);
    ctx.stroke();
    // Tips
    ctx.beginPath();
    ctx.moveTo(Math.cos(-Math.PI * 0.7) * r, Math.sin(-Math.PI * 0.7) * r);
    const tipX = offset + Math.cos(-Math.PI * 0.3) * r * 0.75;
    const tipY = Math.sin(-Math.PI * 0.3) * r * 0.75;
    ctx.lineTo(tipX, tipY);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(Math.cos(Math.PI * 0.7) * r, Math.sin(Math.PI * 0.7) * r);
    ctx.lineTo(offset + Math.cos(Math.PI * 0.3) * r * 0.75, Math.sin(Math.PI * 0.3) * r * 0.75);
    ctx.stroke();
    ctx.restore();
  }
  ctx.lineWidth = lineW;
}

function drawPrism(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const cx = rng() * w;
    const cy = rng() * h;
    const size = w * (0.03 + rng() * 0.08);
    const angle = (rng() - 0.5) * Math.PI;
    ctx.lineWidth = lineW * (0.6 + rng() * 1.2);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    // Front face triangle
    ctx.beginPath();
    ctx.moveTo(0, -size);
    ctx.lineTo(size * 0.87, size * 0.5);
    ctx.lineTo(-size * 0.87, size * 0.5);
    ctx.closePath();
    ctx.stroke();
    // Depth lines for 3D prism effect
    const depth = size * 0.4;
    const offsetX = depth * 0.6;
    const offsetY = -depth * 0.4;
    ctx.lineWidth = lineW * 0.5;
    const verts = [[0, -size], [size * 0.87, size * 0.5], [-size * 0.87, size * 0.5]];
    for (const [vx, vy] of verts) {
      ctx.beginPath();
      ctx.moveTo(vx, vy);
      ctx.lineTo(vx + offsetX, vy + offsetY);
      ctx.stroke();
    }
    // Back face
    ctx.beginPath();
    ctx.moveTo(0 + offsetX, -size + offsetY);
    ctx.lineTo(size * 0.87 + offsetX, size * 0.5 + offsetY);
    ctx.lineTo(-size * 0.87 + offsetX, size * 0.5 + offsetY);
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }
  ctx.lineWidth = lineW;
}

function drawVortex(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const cx = rng() * w;
    const cy = rng() * h;
    const maxR = Math.min(w, h) * (0.08 + rng() * 0.18);
    const arms = 3 + Math.floor(rng() * 4);
    const direction = rng() > 0.5 ? 1 : -1;
    ctx.lineWidth = lineW * (0.5 + rng() * 1.0);
    for (let a = 0; a < arms; a++) {
      const startAngle = (a / arms) * Math.PI * 2;
      const steps = 60;
      ctx.beginPath();
      for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        const r = t * maxR;
        const angle = startAngle + direction * t * Math.PI * 2.5;
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;
        if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
  }
  ctx.lineWidth = lineW;
}

function drawBamboo(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const x = rng() * w;
    const lean = (rng() - 0.5) * w * 0.05;
    const segCount = 4 + Math.floor(rng() * 6);
    const segHeight = h * (0.06 + rng() * 0.06);
    const stemW = w * (0.005 + rng() * 0.008);
    const startY = h * (0.3 + rng() * 0.5);
    ctx.lineWidth = stemW;
    ctx.lineCap = 'butt';
    for (let s = 0; s < segCount; s++) {
      const sy = startY - s * segHeight;
      const ey = sy - segHeight * 0.9;
      const sx = x + lean * s / segCount;
      const ex = x + lean * (s + 1) / segCount;
      // Stem segment
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(ex, ey);
      ctx.stroke();
      // Node ring
      ctx.lineWidth = stemW * 1.8;
      ctx.beginPath();
      ctx.moveTo(sx - stemW * 1.5, sy);
      ctx.lineTo(sx + stemW * 1.5, sy);
      ctx.stroke();
      ctx.lineWidth = stemW;
      // Leaf at upper nodes
      if (s >= 1 && rng() > 0.4) {
        const leafDir = rng() > 0.5 ? 1 : -1;
        const leafAngle = -Math.PI * 0.3 + leafDir * Math.PI * 0.4;
        const leafLen = w * (0.03 + rng() * 0.05);
        ctx.lineWidth = lineW * 0.4;
        ctx.beginPath();
        ctx.moveTo(ex, ey);
        ctx.quadraticCurveTo(
          ex + Math.cos(leafAngle) * leafLen * 0.6,
          ey + Math.sin(leafAngle) * leafLen * 0.6 - leafLen * 0.1,
          ex + Math.cos(leafAngle) * leafLen,
          ey + Math.sin(leafAngle) * leafLen
        );
        ctx.stroke();
        ctx.lineWidth = stemW;
      }
    }
  }
  ctx.lineWidth = lineW;
}

function drawThorns(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const cx = rng() * w;
    const cy = rng() * h;
    const spikes = 5 + Math.floor(rng() * 10);
    const minR = w * (0.01 + rng() * 0.02);
    const maxR = minR + w * (0.02 + rng() * 0.05);
    ctx.lineWidth = lineW * (0.5 + rng() * 1.0);
    for (let s = 0; s < spikes; s++) {
      const angle = (s / spikes) * Math.PI * 2 + rng() * 0.3;
      const r = minR + rng() * (maxR - minR);
      const sideAngle1 = angle - 0.15 - rng() * 0.2;
      const sideAngle2 = angle + 0.15 + rng() * 0.2;
      const baseR = minR * 0.4;
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(sideAngle1) * baseR, cy + Math.sin(sideAngle1) * baseR);
      ctx.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
      ctx.lineTo(cx + Math.cos(sideAngle2) * baseR, cy + Math.sin(sideAngle2) * baseR);
      ctx.stroke();
    }
    // Center dot
    ctx.beginPath();
    ctx.arc(cx, cy, minR * 0.3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.lineWidth = lineW;
}

function drawAurora(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  for (let i = 0; i < count; i++) {
    const baseY = h * (0.1 + rng() * 0.5);
    const amplitude = h * (0.04 + rng() * 0.10);
    const thickness = h * (0.015 + rng() * 0.04);
    const freq1 = 1 + rng() * 2;
    const freq2 = 3 + rng() * 4;
    const phase1 = rng() * Math.PI * 2;
    const phase2 = rng() * Math.PI * 2;
    const steps = 100;
    ctx.lineWidth = lineW * 0.5;
    // Draw multiple thin strands per band
    const strands = 3 + Math.floor(rng() * 4);
    for (let st = 0; st < strands; st++) {
      const yOffset = (st / (strands - 1 || 1) - 0.5) * thickness;
      ctx.beginPath();
      for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        const x = t * w;
        const warp = Math.sin(t * Math.PI * 2 * freq1 + phase1) * amplitude
          + Math.sin(t * Math.PI * 2 * freq2 + phase2) * amplitude * 0.3;
        const y = baseY + warp + yOffset;
        if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    // Curtain-like vertical streaks
    const curtainCount = Math.floor(w / (w * 0.04));
    ctx.lineWidth = lineW * 0.25;
    for (let c = 0; c < curtainCount; c++) {
      if (rng() > 0.6) {
        const cx = (c / curtainCount) * w + rng() * w * 0.02;
        const t = cx / w;
        const baseWarp = Math.sin(t * Math.PI * 2 * freq1 + phase1) * amplitude;
        ctx.beginPath();
        ctx.moveTo(cx, baseY + baseWarp - thickness * 0.5);
        ctx.lineTo(cx + (rng() - 0.5) * w * 0.01, baseY + baseWarp + thickness * 0.8);
        ctx.stroke();
      }
    }
  }
  ctx.lineWidth = lineW;
}

function drawMosaic(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const cellSize = Math.min(w, h) / Math.sqrt(count);
  // Generate irregular polygon tiles using a grid with jitter
  const cols = Math.ceil(w / cellSize) + 1;
  const rows = Math.ceil(h / cellSize) + 1;
  const jitter = cellSize * 0.35;
  // Build jittered grid points
  const pts = [];
  for (let row = 0; row <= rows; row++) {
    pts.push([]);
    for (let col = 0; col <= cols; col++) {
      const jx = (row === 0 || row === rows) ? 0 : (rng() - 0.5) * jitter;
      const jy = (col === 0 || col === cols) ? 0 : (rng() - 0.5) * jitter;
      pts[row].push({
        x: col * cellSize + jx,
        y: row * cellSize + jy
      });
    }
  }
  ctx.lineWidth = lineW * 0.7;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const p = pts[row][col];
      const pr = pts[row][col + 1];
      const pb = pts[row + 1] ? pts[row + 1][col] : null;
      const pbr = pts[row + 1] ? pts[row + 1][col + 1] : null;
      if (!pb || !pbr) continue;
      if (rng() > 0.5) {
        // Split quad into two triangles along one diagonal
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(pr.x, pr.y);
        ctx.lineTo(pbr.x, pbr.y);
        ctx.closePath();
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(pbr.x, pbr.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.closePath();
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(pr.x, pr.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.closePath();
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(pr.x, pr.y);
        ctx.lineTo(pbr.x, pbr.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.closePath();
        ctx.stroke();
      }
    }
  }
  ctx.lineWidth = lineW;
}

function drawBarcode(ctx, w, h, count, rng) {
  const margin = w * 0.05;
  const usableW = w - margin * 2;
  const barH = h * (0.4 + rng() * 0.3);
  const barTop = (h - barH) * (0.3 + rng() * 0.4);
  let x = margin;
  let barIndex = 0;
  while (x < margin + usableW && barIndex < count * 3) {
    const barW = usableW * (0.005 + rng() * 0.018);
    const gap = usableW * (0.003 + rng() * 0.008);
    const tall = rng() > 0.15;
    const thisH = tall ? barH : barH * (0.85 + rng() * 0.1);
    const thisTop = tall ? barTop : barTop + (barH - thisH);
    ctx.fillRect(x, thisTop, barW, thisH);
    x += barW + gap;
    barIndex++;
  }
  // Optional text-like baseline dashes
  const dashY = barTop + barH + h * 0.02;
  x = margin;
  for (let i = 0; i < 10; i++) {
    const dw = usableW * (0.03 + rng() * 0.05);
    const dh = h * 0.008;
    ctx.fillRect(x + rng() * usableW * 0.08, dashY, dw, dh);
    x += usableW * 0.09 + rng() * usableW * 0.02;
    if (x > margin + usableW) break;
  }
}

function drawContour(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  // Multiple overlapping smooth blobs create contour-like rings
  const peaks = 2 + Math.floor(rng() * 4);
  const peakList = [];
  for (let p = 0; p < peaks; p++) {
    peakList.push({
      x: w * (0.15 + rng() * 0.7),
      y: h * (0.15 + rng() * 0.7),
      strength: 0.3 + rng() * 0.7
    });
  }
  function elevation(x, y) {
    let val = 0;
    for (const pk of peakList) {
      const dx = (x - pk.x) / w;
      const dy = (y - pk.y) / h;
      val += pk.strength * Math.exp(-(dx * dx + dy * dy) * 8);
    }
    return val;
  }
  // March a grid of points and draw isolines
  const gridSize = Math.ceil(Math.min(w, h) / 80);
  const cols = Math.ceil(w / gridSize);
  const rows = Math.ceil(h / gridSize);
  ctx.lineWidth = lineW * 0.7;
  for (let level = 1; level <= count; level++) {
    const threshold = level / (count + 1);
    ctx.beginPath();
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x0 = col * gridSize;
        const y0 = row * gridSize;
        const x1 = x0 + gridSize;
        const y1 = y0 + gridSize;
        const v00 = elevation(x0, y0);
        const v10 = elevation(x1, y0);
        const v01 = elevation(x0, y1);
        const v11 = elevation(x1, y1);
        // Simple marching squares: draw segment if threshold crossed on an edge
        const edges = [];
        if ((v00 < threshold) !== (v10 < threshold)) {
          const t = (threshold - v00) / (v10 - v00);
          edges.push({ x: x0 + t * gridSize, y: y0 });
        }
        if ((v10 < threshold) !== (v11 < threshold)) {
          const t = (threshold - v10) / (v11 - v10);
          edges.push({ x: x1, y: y0 + t * gridSize });
        }
        if ((v01 < threshold) !== (v11 < threshold)) {
          const t = (threshold - v01) / (v11 - v01);
          edges.push({ x: x0 + t * gridSize, y: y1 });
        }
        if ((v00 < threshold) !== (v01 < threshold)) {
          const t = (threshold - v00) / (v01 - v00);
          edges.push({ x: x0, y: y0 + t * gridSize });
        }
        if (edges.length >= 2) {
          ctx.moveTo(edges[0].x, edges[0].y);
          ctx.lineTo(edges[1].x, edges[1].y);
        }
      }
    }
    ctx.stroke();
  }
  ctx.lineWidth = lineW;
}

function drawCityscape(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const groundY = h * (0.65 + rng() * 0.2);
  // Generate buildings sorted by x
  const buildings = [];
  let x = 0;
  while (x < w && buildings.length < count) {
    const bw = w * (0.02 + rng() * 0.06);
    const bh = h * (0.05 + Math.pow(rng(), 1.5) * 0.45);
    buildings.push({ x, y: groundY - bh, w: bw, h: bh });
    x += bw + w * (0.003 + rng() * 0.01);
  }
  // Draw buildings
  ctx.lineWidth = lineW * 0.6;
  for (const b of buildings) {
    ctx.beginPath();
    ctx.rect(b.x, b.y, b.w, b.h);
    ctx.stroke();
    // Windows
    const winCols = Math.floor(b.w / (w * 0.008)) || 1;
    const winRows = Math.floor(b.h / (h * 0.04)) || 1;
    const wx = b.w / winCols;
    const wy = b.h / winRows;
    for (let wr = 0; wr < winRows; wr++) {
      for (let wc = 0; wc < winCols; wc++) {
        if (rng() > 0.45) {
          ctx.fillRect(
            b.x + wc * wx + wx * 0.2,
            b.y + wr * wy + wy * 0.2,
            wx * 0.55, wy * 0.55
          );
        }
      }
    }
    // Antenna
    if (rng() > 0.6) {
      ctx.lineWidth = lineW * 0.4;
      ctx.beginPath();
      ctx.moveTo(b.x + b.w * 0.5, b.y);
      ctx.lineTo(b.x + b.w * 0.5, b.y - h * (0.02 + rng() * 0.04));
      ctx.stroke();
      ctx.lineWidth = lineW * 0.6;
    }
  }
  // Ground line
  ctx.lineWidth = lineW;
  ctx.beginPath();
  ctx.moveTo(0, groundY);
  ctx.lineTo(w, groundY);
  ctx.stroke();
  ctx.lineWidth = lineW;
}

function drawFrequency(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const margin = w * 0.06;
  const usableW = w - margin * 2;
  const centerY = h * (0.4 + rng() * 0.2);
  const steps = 150;
  for (let i = 0; i < count; i++) {
    const peakX = margin + rng() * usableW;
    const peakW = usableW * (0.05 + rng() * 0.25);
    const peakH = h * (0.08 + rng() * 0.25) * (rng() > 0.5 ? 1 : -1);
    const sharpness = 1.5 + rng() * 4;
    ctx.lineWidth = lineW * (0.4 + rng() * 0.8);
    ctx.beginPath();
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const x = margin + t * usableW;
      const dist = (x - peakX) / peakW;
      const y = centerY - peakH * Math.exp(-Math.pow(dist, 2) * sharpness);
      if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  // Baseline
  ctx.lineWidth = lineW * 0.5;
  ctx.beginPath();
  ctx.moveTo(margin, centerY);
  ctx.lineTo(margin + usableW, centerY);
  ctx.stroke();
  ctx.lineWidth = lineW;
}

function drawParticles(ctx, w, h, count, rng) {
  const lineW = ctx.lineWidth;
  const freq1 = 0.002 + rng() * 0.003;
  const freq2 = 0.003 + rng() * 0.004;
  const phase1 = rng() * 100;
  const phase2 = rng() * 100;
  function flowAngle(x, y) {
    return Math.sin(x * freq1 + phase1) * Math.cos(y * freq1 + phase2) * Math.PI
      + Math.sin(x * freq2 + y * freq2) * Math.PI * 0.5;
  }
  const arrowSize = w * (0.006 + rng() * 0.006);
  ctx.lineWidth = lineW * 0.7;
  for (let i = 0; i < count; i++) {
    const x = rng() * w;
    const y = rng() * h;
    const angle = flowAngle(x, y);
    // Draw small triangle arrow
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(arrowSize, 0);
    ctx.lineTo(-arrowSize * 0.6, -arrowSize * 0.5);
    ctx.lineTo(-arrowSize * 0.3, 0);
    ctx.lineTo(-arrowSize * 0.6, arrowSize * 0.5);
    ctx.closePath();
    if (rng() > 0.5) ctx.fill(); else ctx.stroke();
    ctx.restore();
  }
  ctx.lineWidth = lineW;
}

/** All available line art styles with metadata */
export const LINE_ART_STYLES = [
  { id: 'none', label: 'None' },
  { id: 'flow', label: 'Flow Field' },
  { id: 'grid', label: 'Broken Grid' },
  { id: 'circles', label: 'Orbital Rings' },
  { id: 'topo', label: 'Topographic' },
  { id: 'spectrograph', label: 'Spectrograph' },
  { id: 'waveform', label: 'Waveform' },
  { id: 'fire', label: 'Fire' },
  { id: 'oscillator', label: 'Oscillator' },
  { id: 'smiley', label: 'Smiley' },
  { id: 'constellation', label: 'Constellation' },
  { id: 'circuit', label: 'Circuit' },
  { id: 'chevron', label: 'Chevron' },
  { id: 'diamonds', label: 'Diamonds' },
  { id: 'crosshatch', label: 'Crosshatch' },
  { id: 'dots', label: 'Dots' },
  { id: 'spiral', label: 'Spiral' },
  { id: 'maze', label: 'Maze' },
  { id: 'lightning', label: 'Lightning' },
  { id: 'rain', label: 'Rain' },
  { id: 'hexgrid', label: 'Hex Grid' },
  { id: 'scatter', label: 'Scatter' },
  { id: 'waves', label: 'Waves' },
  { id: 'bubbles', label: 'Bubbles' },
  { id: 'branches', label: 'Branches' },
  { id: 'ribbon', label: 'Ribbon' },
  { id: 'static', label: 'Static' },
  { id: 'dna', label: 'DNA' },
  { id: 'radar', label: 'Radar' },
  { id: 'pulse', label: 'Pulse' },
  { id: 'shatter', label: 'Shatter' },
  { id: 'weave', label: 'Weave' },
  { id: 'feather', label: 'Feather' },
  { id: 'ripple', label: 'Ripple' },
  { id: 'glitch', label: 'Glitch' },
  { id: 'meteor', label: 'Meteor' },
  { id: 'terrain', label: 'Terrain' },
  { id: 'lattice', label: 'Lattice' },
  { id: 'crescents', label: 'Crescents' },
  { id: 'prism', label: 'Prism' },
  { id: 'vortex', label: 'Vortex' },
  { id: 'bamboo', label: 'Bamboo' },
  { id: 'thorns', label: 'Thorns' },
  { id: 'aurora', label: 'Aurora' },
  { id: 'mosaic', label: 'Mosaic' },
  { id: 'barcode', label: 'Barcode' },
  { id: 'contour', label: 'Contour' },
  { id: 'cityscape', label: 'Cityscape' },
  { id: 'frequency', label: 'Frequency' },
  { id: 'particles', label: 'Particles' },
];
